// ===============================================================
// V·IANet Requests (M16) — GruposTab: CRUD de GrupoSolicitud
// ===============================================================

import { useState } from 'react';
import { useI18n } from '../../../../i18n';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { useRequestsConfig } from '../hooks/useRequestsConfig';
import type { GrupoSolicitud } from '../types/requests.types';

interface GrupoForm {
  nombre: string;
  descripcion: string;
  icono: string;
  orden: number;
}

const EMPTY_FORM: GrupoForm = { nombre: '', descripcion: '', icono: '', orden: 0 };

export function GruposTab() {
  const { t } = useI18n();
  const { items, loading, crear, actualizar, eliminar } =
    useRequestsConfig<GrupoSolicitud>('/requests/grupos');

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<GrupoForm>(EMPTY_FORM);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (g: GrupoSolicitud) => {
    setEditId(g.id);
    setForm({
      nombre: g.nombre,
      descripcion: g.descripcion ?? '',
      icono: g.icono ?? '',
      orden: g.orden,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditId(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    try {
      if (editId) {
        await actualizar(editId, form);
      } else {
        await crear(form);
      }
      closeModal();
    } catch {
      // error handled by hook
    }
  };

  const handleToggleActivo = async (g: GrupoSolicitud) => {
    try {
      await actualizar(g.id, { activo: !g.activo } as Partial<GrupoSolicitud>);
    } catch {
      // error handled by hook
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await eliminar(confirmDeleteId);
    } catch {
      // error handled by hook
    }
    setConfirmDeleteId(null);
  };

  if (loading) {
    return <LoadingSpinner size="sm" muted />;
  }

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#1F3864' }}>
          {t.requests?.grupos ?? 'Grupos de solicitud'}
        </h3>
        <button
          onClick={openCreate}
          style={{
            padding: '8px 20px',
            borderRadius: 8,
            border: 'none',
            background: '#1F3864',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {t.requests?.nuevo ?? 'Nuevo grupo'}
        </button>
      </div>

      {/* Table */}
      {items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#9CA3AF' }}>
          {t.requests?.sinDatos ?? 'No hay grupos configurados'}
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
              <th
                style={{
                  textAlign: 'left',
                  padding: '12px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#6B7280',
                }}
              >
                Nombre
              </th>
              <th
                style={{
                  textAlign: 'left',
                  padding: '12px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#6B7280',
                }}
              >
                Descripcion
              </th>
              <th
                style={{
                  textAlign: 'center',
                  padding: '12px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#6B7280',
                }}
              >
                Orden
              </th>
              <th
                style={{
                  textAlign: 'center',
                  padding: '12px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#6B7280',
                }}
              >
                Activo
              </th>
              <th
                style={{
                  textAlign: 'right',
                  padding: '12px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#6B7280',
                }}
              >
                Acciones
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((g) => (
              <tr key={g.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 500 }}>{g.nombre}</td>
                <td style={{ padding: '12px 16px', fontSize: 14, color: '#6B7280' }}>
                  {g.descripcion ?? '-'}
                </td>
                <td style={{ padding: '12px 16px', fontSize: 14, textAlign: 'center' }}>
                  {g.orden}
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  <button
                    onClick={() => handleToggleActivo(g)}
                    style={{
                      width: 40,
                      height: 22,
                      borderRadius: 11,
                      border: 'none',
                      background: g.activo ? '#33BCE1' : '#D1D5DB',
                      cursor: 'pointer',
                      position: 'relative',
                      transition: 'background 0.2s',
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        top: 2,
                        left: g.activo ? 20 : 2,
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        background: '#fff',
                        transition: 'left 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }}
                    />
                  </button>
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                  <button
                    onClick={() => openEdit(g)}
                    title={t.requests?.editar ?? 'Editar'}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 4,
                      marginRight: 4,
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M11.5 1.5l3 3L5 14H2v-3L11.5 1.5z"
                        stroke="#2F5496"
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => setConfirmDeleteId(g.id)}
                    title={t.requests?.eliminar ?? 'Eliminar'}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M3 4h10M5.5 4V3a1 1 0 011-1h3a1 1 0 011 1v1M6 7v5M10 7v5M4 4l.7 9a1 1 0 001 .9h4.6a1 1 0 001-.9L12 4"
                        stroke="#DC2626"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 32,
              width: 480,
              maxHeight: '80vh',
              overflow: 'auto',
            }}
          >
            <h4 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 600, color: '#1F3864' }}>
              {editId
                ? (t.requests?.editar ?? 'Editar grupo')
                : (t.requests?.nuevo ?? 'Nuevo grupo')}
            </h4>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>
                Nombre *
                <input
                  value={form.nombre}
                  onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
                  style={{
                    display: 'block',
                    width: '100%',
                    marginTop: 4,
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid #D1D5DB',
                    fontSize: 14,
                    boxSizing: 'border-box',
                  }}
                />
              </label>

              <label style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>
                Descripcion
                <textarea
                  value={form.descripcion}
                  onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))}
                  rows={3}
                  style={{
                    display: 'block',
                    width: '100%',
                    marginTop: 4,
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid #D1D5DB',
                    fontSize: 14,
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                />
              </label>

              <label style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>
                Icono (nombre SVG)
                <input
                  value={form.icono}
                  onChange={(e) => setForm((f) => ({ ...f, icono: e.target.value }))}
                  style={{
                    display: 'block',
                    width: '100%',
                    marginTop: 4,
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid #D1D5DB',
                    fontSize: 14,
                    boxSizing: 'border-box',
                  }}
                />
              </label>

              <label style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>
                Orden
                <input
                  type="number"
                  value={form.orden}
                  onChange={(e) => setForm((f) => ({ ...f, orden: Number(e.target.value) }))}
                  style={{
                    display: 'block',
                    width: '100%',
                    marginTop: 4,
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid #D1D5DB',
                    fontSize: 14,
                    boxSizing: 'border-box',
                  }}
                />
              </label>
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
              <button
                onClick={closeModal}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: '1px solid #D1D5DB',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                {t.requests?.cancelar ?? 'Cancelar'}
              </button>
              <button
                onClick={handleSave}
                disabled={!form.nombre.trim()}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: form.nombre.trim() ? '#1F3864' : '#9CA3AF',
                  color: '#fff',
                  cursor: form.nombre.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                {t.common.guardar}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      {confirmDeleteId && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: 400 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1F3864' }}>
              {t.requests?.confirmarEliminar ?? 'Confirmar eliminacion'}
            </h4>
            <p style={{ margin: '0 0 24px', fontSize: 14, color: '#6B7280' }}>
              {t.requests?.confirmarEliminarMsg ??
                'Esta accion no se puede deshacer. Se eliminara el grupo y sus dependencias.'}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmDeleteId(null)}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: '1px solid #D1D5DB',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                {t.requests?.cancelar ?? 'Cancelar'}
              </button>
              <button
                onClick={handleDelete}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#DC2626',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                {t.requests?.eliminar ?? 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
